/**
 * ViewModeToggle (v6.36.22) — toggle pill between "Student View" and
 * "Teacher View". Used on My Feedback + My Progress pages so the
 * audience can flip between kid-friendly translations and the full
 * technical view.
 *
 * Persists the choice to localStorage so the same choice survives a
 * page reload.
 */
import React from 'react';
import { Sparkles, BarChart3 } from 'lucide-react';

const STORAGE_KEY = 'mosaic_student_view_mode';

export function useViewMode(defaultMode = 'student') {
  const [mode, setModeState] = React.useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || defaultMode;
    } catch (_e) {
      return defaultMode;
    }
  });

  const setMode = React.useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_e) { /* ignore */ }
  }, []);

  return [mode, setMode];
}

const ViewModeToggle = ({ mode, onChange }) => {
  const baseBtn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold transition-colors';
  const active = 'bg-[#002FA7] text-white';
  const inactive = 'text-gray-600 hover:text-[#002FA7]';

  return (
    <div
      className="inline-flex border-2 border-[#002FA7] rounded-full overflow-hidden"
      data-testid="view-mode-toggle"
      role="tablist"
      aria-label="Switch between student and teacher view"
    >
      <button
        type="button"
        onClick={() => onChange('student')}
        className={`${baseBtn} ${mode === 'student' ? active : inactive}`}
        data-testid="view-mode-student"
        role="tab"
        aria-selected={mode === 'student'}
      >
        <Sparkles size={14} /> Student View
      </button>
      <button
        type="button"
        onClick={() => onChange('teacher')}
        className={`${baseBtn} ${mode === 'teacher' ? active : inactive}`}
        data-testid="view-mode-teacher"
        role="tab"
        aria-selected={mode === 'teacher'}
      >
        <BarChart3 size={14} /> Teacher View
      </button>
    </div>
  );
};

export default ViewModeToggle;
